import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";

import { cn } from "@/lib/utils";

// Types for popover positioning variants
export type PopoverPosition =
  | "top"
  | "bottom"
  | "left"
  | "right"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

export type PopoverType = "default" | "arrow-none";

export interface PopoverContentProps
  extends React.ComponentProps<typeof PopoverPrimitive.Content> {
  position?: PopoverPosition;
  type?: PopoverType;
  showArrow?: boolean;
  autoFocus?: boolean;
}

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  position = "bottom",
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: PopoverContentProps) {
  // Determine side and align based on position
  const getSideAndAlign = (pos: PopoverPosition) => {
    switch (pos) {
      case "top":
        return { side: "top" as const, align: "center" as const };
      case "bottom":
        return { side: "bottom" as const, align: "center" as const };
      case "left":
        return { side: "left" as const, align: "center" as const };
      case "right":
        return { side: "right" as const, align: "center" as const };
      case "top-left":
        return { side: "top" as const, align: "start" as const };
      case "top-right":
        return { side: "top" as const, align: "end" as const };
      case "bottom-left":
        return { side: "bottom" as const, align: "start" as const };
      case "bottom-right":
        return { side: "bottom" as const, align: "end" as const };
      default:
        return { side: "bottom" as const, align: "center" as const };
    }
  };

  const { side, align: calculatedAlign } = getSideAndAlign(position);
  const finalAlign = align === "center" ? calculatedAlign : align;

  // Determine if arrows should be shown
  const shouldShowArrow =
    showArrow !== undefined ? showArrow : type === "default";

  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        data-position={position}
        data-type={type}
        data-no-focus="true"
        side={side}
        align={finalAlign}
        sideOffset={sideOffset}
        className={cn("wl-popover-content", className)}
        autoFocus={autoFocus}
        onOpenAutoFocus={(e) => {
          // Prevent automatic focus when popover opens
          e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          // Prevent automatic focus when popover closes
          e.preventDefault();
        }}
        tabIndex={-1}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />;
}

// Convenience components for specific positions
function PopoverContentTop({
  className,
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: Omit<PopoverContentProps, "position">) {
  return (
    <PopoverContent
      position="top"
      type={type}
      showArrow={showArrow}
      autoFocus={autoFocus}
      className={cn("wl-popover-content-top", className)}
      {...props}
    />
  );
}

function PopoverContentBottom({
  className,
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: Omit<PopoverContentProps, "position">) {
  return (
    <PopoverContent
      position="bottom"
      type={type}
      showArrow={showArrow}
      autoFocus={autoFocus}
      className={cn("wl-popover-content-bottom", className)}
      {...props}
    />
  );
}

function PopoverContentLeft({
  className,
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: Omit<PopoverContentProps, "position">) {
  return (
    <PopoverContent
      position="left"
      type={type}
      showArrow={showArrow}
      autoFocus={autoFocus}
      className={cn("wl-popover-content-left", className)}
      {...props}
    />
  );
}

function PopoverContentRight({
  className,
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: Omit<PopoverContentProps, "position">) {
  return (
    <PopoverContent
      position="right"
      type={type}
      showArrow={showArrow}
      autoFocus={autoFocus}
      className={cn("wl-popover-content-right", className)}
      {...props}
    />
  );
}

function PopoverContentTopLeft({
  className,
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: Omit<PopoverContentProps, "position">) {
  return (
    <PopoverContent
      position="top-left"
      type={type}
      showArrow={showArrow}
      autoFocus={autoFocus}
      className={cn("wl-popover-content-top-left", className)}
      {...props}
    />
  );
}

function PopoverContentTopRight({
  className,
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: Omit<PopoverContentProps, "position">) {
  return (
    <PopoverContent
      position="top-right"
      type={type}
      showArrow={showArrow}
      autoFocus={autoFocus}
      className={cn("wl-popover-content-top-right", className)}
      {...props}
    />
  );
}

function PopoverContentBottomLeft({
  className,
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: Omit<PopoverContentProps, "position">) {
  return (
    <PopoverContent
      position="bottom-left"
      type={type}
      showArrow={showArrow}
      autoFocus={autoFocus}
      className={cn("wl-popover-content-bottom-left", className)}
      {...props}
    />
  );
}

function PopoverContentBottomRight({
  className,
  type = "default",
  showArrow,
  autoFocus = false,
  ...props
}: Omit<PopoverContentProps, "position">) {
  return (
    <PopoverContent
      position="bottom-right"
      type={type}
      showArrow={showArrow}
      autoFocus={autoFocus}
      className={cn("wl-popover-content-bottom-right", className)}
      {...props}
    />
  );
}

export {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverAnchor,
  PopoverContentTop,
  PopoverContentBottom,
  PopoverContentLeft,
  PopoverContentRight,
  PopoverContentTopLeft,
  PopoverContentTopRight,
  PopoverContentBottomLeft,
  PopoverContentBottomRight,
};
